import React, { useState, useEffect, useMemo } from 'react';
import API from '../services/api';
import { API_BASE_URL } from '../constants/config';
import { THEME } from '../constants/theme'; 

// PANELS IMPORT
import DashboardHome from '../components/Panels/DashboardHome';
import SalesmanPanel from '../components/Panels/SalesmanPanel';
import AccountantPanel from '../components/Panels/AccountantPanel';
import PurchasePanel from '../components/Panels/PurchasePanel';
import ETOPanel from '../components/Panels/ETOPanel'; // Aap chahein toh iska import alias ya file name bhi change kar sakte hain
import HRPanel from '../components/Panels/HRPanel';
import CustomerCarePanel from '../components/Panels/CustomerCarePanel';
import ComplaintsModule from '../components/Panels/ComplaintsModule';

const EmployeeDashboard = () => {
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });

    // 🏠 Default landing tab ab hamesha "dashboard" (Dashboard Overview) hai
    const [activeTab, setActiveTab] = useState('dashboard');

    // Jab Quick Action card click ho, ye batata hai ke target panel ke andar
    // kaunsa sub-tab / form seedha khulna chahiye (e.g. "add_purchase")
    const [pendingSubTab, setPendingSubTab] = useState(null);

    useEffect(() => {
        if (!user) {
            window.location.href = '/login';
        }
    }, [user]);

    const rolesArray = useMemo(() => {
        return user?.roles ? user.roles.split(',').filter(r => r.trim() !== "") : [];
    }, [user?.roles]);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/login';
    };

    // 🔐 Dashboard Overview ke Quick Action cards yahan se navigate karte hain.
    // Agar user ke paas permission na ho, DashboardHome khud hi onClick ko ignore
    // kar deta hai (card disabled/greyed dikhta hai), isliye yahan dobara check
    // karne ki zaroorat nahi — lekin safety ke liye rolesArray check rakha hai.
    const handleNavigate = (role, subTab) => {
        if (!role || !rolesArray.includes(role)) return; // 🔒 permission na ho to kuch na ho
        setPendingSubTab(subTab || null);
        setActiveTab(role);
    };

    const getIcon = (role) => {
        const icons = {
            'dashboard': '🏠',
            'Salesman': '🛒',
            'Accountant': '💰',
            'Purchase_Manager': '📦',
            'Doc_Department': '📑', // Ya agar database mein role change kiya hai toh 'Doc_Department' rakh dein
            'ETO_Manager': '📑', // Same icon for old role name
            'HR_Manager': '👥',
            'Customer_Care': '☎️'
        };
        return icons[role] || '🛡️';
    };

    // Helper function to display clean names on UI
    const formatRoleName = (role) => {
        if (role === 'dashboard') return 'Dashboard';
        if (role === 'ETO_Manager') return 'Doc Department';
        return role.replace('_', ' ');
    };

    const renderContent = () => {
        if (activeTab === 'dashboard') {
            return (
                <DashboardHome
                    rolesArray={rolesArray}
                    userName={user?.full_name}
                    onNavigate={handleNavigate}
                />
            );
        }

        if (rolesArray.length === 0) return (
            <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100%', color: '#94a3b8'}}>
                <h3>No Panels Assigned. Contact Admin.</h3>
            </div>
        );

        switch (activeTab) {
            case 'Salesman': return <SalesmanPanel />;
            case 'Accountant': return <AccountantPanel initialTab={pendingSubTab} />;
            case 'Purchase_Manager': return <PurchasePanel initialSubTab={pendingSubTab} />;
            case 'Doc_Department':
            case 'ETO_Manager':
                return <ETOPanel />; // Role match ke mutabiq component render hoga (dono possible role names handle)
            case 'HR_Manager': return <HRPanel />;
            case 'Customer_Care': return <ComplaintsModule initialView={pendingSubTab} />;
            default: return <div style={{padding: '50px', textAlign: 'center'}}>Select a panel.</div>;
        }
    };

    if (!user) return null;

    return (
        <div style={styles.container}>
            {/* --- SIDEBAR --- */}
            <aside style={styles.sidebar}>
               <div style={styles.logoContainer}>
                    <div style={styles.logoIcon}>UM</div>
                    <div style={styles.logoText}>
                        <span style={{ fontWeight: '800', fontSize: '18px', color: '#fff' }}>
                            {THEME.footerCompany.toUpperCase()}
                        </span>
                    </div>
                </div>

                <div style={styles.navHeader}>MAIN MENU</div>
                <nav style={{ flex: 1, padding: '0 10px', overflowY: 'auto' }}>
                    {/* 🏠 Dashboard Overview — hamesha sab ke liye visible */}
                    <div
                        key="dashboard"
                        onClick={() => setActiveTab('dashboard')}
                        style={activeTab === 'dashboard' ? styles.activeNav : styles.navItem}
                    >
                        <span style={styles.iconSpan}>{getIcon('dashboard')}</span>
                        <span style={{flex: 1}}>Dashboard</span>
                        {activeTab === 'dashboard' && <div style={styles.activeDot} />}
                    </div>

                    {rolesArray.map((role) => (
                        <div
                            key={role}
                            onClick={() => { setPendingSubTab(null); setActiveTab(role); }}
                            style={activeTab === role ? styles.activeNav : styles.navItem}
                        >
                            <span style={styles.iconSpan}>{getIcon(role)}</span>
                            <span style={{flex: 1}}>{formatRoleName(role)}</span> {/* Updated Name here */}
                            {activeTab === role && <div style={styles.activeDot} />}
                        </div>
                    ))}
                </nav>

                <div style={styles.sidebarFooter}>
                    <button onClick={handleLogout} style={styles.logoutBtn}>
                        <span style={{marginRight: '10px'}}>🚪</span> Logout
                    </button>
                </div>
            </aside>

            {/* --- MAIN CONTENT --- */}
            <div style={styles.mainArea}>
                <header style={styles.header}>
                    <div>
                        <h2 style={styles.tabTitle}>
                            {activeTab === 'dashboard' ? 'Dashboard Overview' : `${formatRoleName(activeTab)} Panel`}
                        </h2>
                        <div style={styles.breadcrumb}>
                            {activeTab === 'dashboard' ? 'Dashboard / Overview' : `Dashboard / ${formatRoleName(activeTab)}`}
                        </div>
                    </div>
                    <div style={styles.profile}>
                        <div style={{textAlign: 'right', marginRight: '12px'}}>
                            <div style={styles.userName}>{user.full_name}</div>
                            <div style={styles.userStatus}>● Online</div>
                        </div>
                        <div style={styles.avatar}>{user.full_name?.charAt(0)}</div>
                    </div>
                </header>

                <main style={styles.content}>
                    {renderContent()}
                </main>
                
                <footer style={styles.footer}>
                    <div>© {THEME.year} {THEME.footerCompany} | Portal v{THEME.footerVersion || '1.0'}</div>
                </footer>
            </div>
        </div>
    );
};

const styles = {
    container: { display: 'flex', height: '100vh', backgroundColor: '#f8fafc', fontFamily: "'Segoe UI', Roboto, sans-serif" },
    sidebar: { width: '270px', backgroundColor: '#0f172a', display: 'flex', flexDirection: 'column', boxShadow: '4px 0 10px rgba(0,0,0,0.1)', zIndex: 10 },
    logoContainer: { padding: '30px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
    logoIcon: { background: THEME.secondary, color: 'white', width: '40px', height: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '10px', fontWeight: 'bold', boxShadow: `0 4px 12px ${THEME.secondary}55` },
    navHeader: { padding: '25px 25px 10px', fontSize: '11px', fontWeight: 'bold', color: '#64748b', letterSpacing: '1.5px' },
    navItem: { display: 'flex', alignItems: 'center', padding: '12px 15px', color: '#94a3b8', cursor: 'pointer', borderRadius: '12px', marginBottom: '6px', transition: 'all 0.3s ease', fontSize: '14px' },
    activeNav: { display: 'flex', alignItems: 'center', padding: '12px 15px', color: '#fff', backgroundColor: THEME.secondary, borderRadius: '12px', marginBottom: '6px', fontWeight: '600', boxShadow: `0 8px 15px ${THEME.secondary}33`, fontSize: '14px' },
    iconSpan: { marginRight: '12px', fontSize: '18px' },
    activeDot: { width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#fff' },
    mainArea: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { height: '80px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 35px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' },
    tabTitle: { margin: 0, fontSize: '22px', fontWeight: '700', color: '#1e293b' },
    breadcrumb: { fontSize: '12px', color: '#94a3b8', marginTop: '4px' },
    profile: { display: 'flex', alignItems: 'center' },
    userName: { fontWeight: '700', color: '#1e293b', fontSize: '15px' },
    userStatus: { fontSize: '11px', color: '#10b981', fontWeight: 'bold' },
    avatar: { width: '45px', height: '45px', background: '#f1f5f9', color: THEME.secondary, borderRadius: '14px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', fontSize: '18px', border: '1px solid #e2e8f0' },
    content: { flex: 1, padding: '30px', overflowY: 'auto', backgroundColor: '#f8fafc' },
    sidebarFooter: { padding: '20px', borderTop: '1px solid rgba(255,255,255,0.05)' },
    logoutBtn: { width: '100%', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', transition: '0.3s' },
    footer: { height: '50px', padding: '0 35px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#64748b', background: '#fff', borderTop: '1px solid #f1f5f9' }
};

export default EmployeeDashboard;
